import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { HeartHandshake, Save, Loader2, AlertTriangle } from "lucide-react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";

const BAND_META = {
    severe: { color: "text-[#9E2A2B]", bg: "bg-[#F9ECEC]", border: "border-[#9E2A2B]", label: "Sobrecarga severa" },
    moderate: { color: "text-[#C85A47]", bg: "bg-[#FBEEE6]", border: "border-[#C85A47]", label: "Sobrecarga moderada" },
    mild: { color: "text-[#9C6E28]", bg: "bg-[#FBF4E5]", border: "border-[#D4A373]", label: "Sobrecarga leve" },
    none: { color: "text-[#556B2F]", bg: "bg-[#EDF3E8]", border: "border-[#556B2F]", label: "Sem sobrecarga" },
};

export default function CaregiverMonitoring() {
    const [params] = useSearchParams();
    const preSelectedPatient = params.get("patient");
    const [overview, setOverview] = useState([]);
    const [patients, setPatients] = useState([]);
    const [items, setItems] = useState([]);
    const [scale, setScale] = useState([]);
    const [selected, setSelected] = useState(preSelectedPatient || "");
    const [caregiverName, setCaregiverName] = useState("");
    const [caregiverEmail, setCaregiverEmail] = useState("");
    const [answers, setAnswers] = useState([]);
    const [notes, setNotes] = useState("");
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const [z, p, ov] = await Promise.all([
                api.get("/monitoring/zarit/items"),
                api.get("/patients"),
                api.get("/monitoring/caregivers-overview"),
            ]);
            setItems(z.data.items); setScale(z.data.scale);
            setPatients(p.data);
            setOverview(ov.data);
            setAnswers(new Array(z.data.items.length).fill(null));
            if (!selected && p.data.length > 0 && !preSelectedPatient) setSelected(p.data[0].id);
        })();
    }, []);

    useEffect(() => {
        if (!selected) return;
        (async () => {
            const { data } = await api.get(`/monitoring/zarit/patient/${selected}`);
            setHistory(data);
            const p = patients.find((x) => x.id === selected);
            if (p) {
                setCaregiverName(p.caregiver_name || "");
                setCaregiverEmail("");
            }
        })();
    }, [selected, patients.length]);

    const submit = async (e) => {
        e.preventDefault();
        if (answers.some((a) => a === null)) {
            toast.error("Responda todas as 7 questões antes de salvar.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/monitoring/zarit", {
                patient_id: selected,
                caregiver_name: caregiverName || null,
                caregiver_email: caregiverEmail || null,
                answers,
                notes,
            });
            setResult(data);
            toast.success("Avaliação Zarit registrada");
            // refresh history + overview
            const [h, ov] = await Promise.all([
                api.get(`/monitoring/zarit/patient/${selected}`),
                api.get("/monitoring/caregivers-overview"),
            ]);
            setHistory(h.data); setOverview(ov.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const reset = () => {
        setAnswers(new Array(items.length).fill(null));
        setNotes(""); setResult(null);
    };

    const totalCounts = {
        severe: overview.filter((o) => o.last_zarit?.band === "severe").length,
        moderate: overview.filter((o) => o.last_zarit?.band === "moderate").length,
        mild: overview.filter((o) => o.last_zarit?.band === "mild").length,
        none: overview.filter((o) => o.last_zarit?.band === "none").length,
        unassessed: overview.filter((o) => !o.last_zarit).length,
    };

    return (
        <AppShell>
            <div className="mb-8">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">Cuidar de quem cuida</div>
                <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand">Acompanhamento do cuidador</h1>
                <p className="text-[var(--text-secondary)] mt-1 max-w-3xl">
                    Escala de sobrecarga de Zarit (versão curta 7 itens) com estratégias de intervenção baseadas em manuais gerontológicos.
                    O adoecimento do cuidador é fator de risco direto para piora do idoso.
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
                <StatCard color="text-[#9E2A2B]" label="Severa" value={totalCounts.severe} />
                <StatCard color="text-[#C85A47]" label="Moderada" value={totalCounts.moderate} />
                <StatCard color="text-[#9C6E28]" label="Leve" value={totalCounts.mild} />
                <StatCard color="text-[#556B2F]" label="Sem sobrecarga" value={totalCounts.none} />
                <StatCard color="text-muted-clinical" label="Sem avaliação" value={totalCounts.unassessed} />
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <div className="card-earthy">
                        <h2 className="font-display text-xl font-medium text-brand mb-4">Aplicar avaliação</h2>
                        <form onSubmit={submit} className="space-y-4">
                            <div className="grid md:grid-cols-3 gap-3">
                                <div>
                                    <label className="label-clinical">Paciente</label>
                                    <select data-testid="zarit-patient-select" required value={selected} onChange={(e) => setSelected(e.target.value)} className="input-clinical">
                                        <option value="">Selecione...</option>
                                        {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="label-clinical">Nome do cuidador</label>
                                    <input value={caregiverName} onChange={(e) => setCaregiverName(e.target.value)} className="input-clinical" placeholder="Ex.: Maria (filha)" />
                                </div>
                                <div>
                                    <label className="label-clinical">E-mail (opcional)</label>
                                    <input type="email" value={caregiverEmail} onChange={(e) => setCaregiverEmail(e.target.value)} className="input-clinical" />
                                </div>
                            </div>

                            <div className="divider-organic" />

                            {items.map((q, i) => (
                                <div key={i} data-testid={`zarit-item-${i}`}>
                                    <div className="text-sm font-medium text-brand mb-2">{i + 1}. {q}</div>
                                    <div className="flex flex-wrap gap-2">
                                        {scale.map((s) => {
                                            const on = answers[i] === s.value;
                                            return (
                                                <button
                                                    key={s.value}
                                                    type="button"
                                                    onClick={() => setAnswers(answers.map((a, j) => (j === i ? s.value : a)))}
                                                    data-testid={`zarit-item-${i}-answer-${s.value}`}
                                                    className={`text-sm px-3 py-1.5 rounded-full transition-colors ${on ? "bg-brand text-white" : "bg-alt text-brand hover:bg-[#EAE4D8]"}`}
                                                >
                                                    {s.value} — {s.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}

                            <div>
                                <label className="label-clinical">Observações</label>
                                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="input-clinical" />
                            </div>

                            <div className="flex gap-2">
                                <button data-testid="zarit-submit-btn" disabled={saving || !selected} className="btn-primary">
                                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Registrar avaliação
                                </button>
                                {result && <button type="button" onClick={reset} className="btn-secondary">Nova avaliação</button>}
                            </div>
                        </form>

                        {result && (
                            <div className={`mt-6 p-4 rounded-xl border-l-4 ${BAND_META[result.band].bg} ${BAND_META[result.band].border}`}>
                                <div className={`text-xs uppercase tracking-wider mb-1 ${BAND_META[result.band].color}`}>Resultado</div>
                                <div className="flex items-baseline gap-3 mb-3">
                                    <div className={`font-display text-2xl font-semibold ${BAND_META[result.band].color}`}>{result.score}/28</div>
                                    <div className={`text-sm font-medium ${BAND_META[result.band].color}`}>{BAND_META[result.band].label}</div>
                                </div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-brand-secondary mb-2">Estratégias recomendadas</div>
                                <ul className="text-sm space-y-1">
                                    {result.recommendations.map((r, i) => (
                                        <li key={i} className="flex items-start gap-2"><span className="text-[var(--brand-accent)] mt-1">•</span><span>{r}</span></li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {history.length > 0 && (
                        <div className="card-earthy mt-4">
                            <h3 className="font-display text-lg font-medium text-brand mb-3">Histórico deste paciente</h3>
                            <div className="space-y-2">
                                {history.map((h) => {
                                    const m = BAND_META[h.band];
                                    return (
                                        <div key={h.id} className={`p-3 rounded-lg ${m.bg} border-l-4 ${m.border}`}>
                                            <div className="flex items-center justify-between text-sm">
                                                <div>
                                                    <div className={`font-medium ${m.color}`}>{h.score}/28 · {h.label}</div>
                                                    <div className="text-xs text-muted-clinical">{new Date(h.created_at).toLocaleString("pt-BR")} · {h.caregiver_name || "—"}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="lg:col-span-1">
                    <div className="card-earthy sticky top-24">
                        <div className="flex items-center gap-2 mb-3">
                            <HeartHandshake size={18} className="text-brand" />
                            <h3 className="font-display text-lg font-medium text-brand">Panorama de cuidadores</h3>
                        </div>
                        {overview.length === 0 ? (
                            <p className="text-sm text-muted-clinical">Nenhum cuidador registrado.</p>
                        ) : (
                            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                                {overview.map((o) => {
                                    const band = o.last_zarit?.band;
                                    const m = band ? BAND_META[band] : null;
                                    return (
                                        <button
                                            key={o.patient_id}
                                            onClick={() => setSelected(o.patient_id)}
                                            data-testid={`caregiver-row-${o.patient_id}`}
                                            className={`w-full text-left p-2.5 rounded-lg text-sm transition-colors ${selected === o.patient_id ? "bg-brand text-white" : "bg-alt hover:bg-[#EAE4D8]"}`}
                                        >
                                            <div className="font-medium">{o.caregiver_name || "—"}</div>
                                            <div className={`text-xs ${selected === o.patient_id ? "opacity-75" : "text-muted-clinical"}`}>
                                                cuidando de {o.patient_name}
                                            </div>
                                            {m && <div className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${selected === o.patient_id ? "bg-white/20" : m.bg + " " + m.color}`}>
                                                {m.label} · {o.last_zarit.score}/28
                                            </div>}
                                            {!m && <div className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${selected === o.patient_id ? "bg-white/20" : "bg-white text-muted-clinical"}`}>
                                                Sem avaliação
                                            </div>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AppShell>
    );
}

function StatCard({ color, label, value }) {
    return (
        <div className="card-earthy">
            <div className="text-xs uppercase tracking-wider text-muted-clinical mb-1">{label}</div>
            <div className={`font-display text-2xl font-semibold ${color}`}>{value}</div>
        </div>
    );
}
